import React, { Component } from 'react';

import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';

import { CollectionReasonGroup } from '~/components/Refund/types';

import { ITEM_TYPE_CAR_HIRE, ITEM_TYPE_SERVICE_FEE, ITEM_TYPE_SUBSCRIPTION, ITEM_TYPE_TOUR } from '~/consts/order';
import {
  CAR_HIRE_REASONS,
  COLLECTION_REASONS,
  COLLECTION_REASON_KEY_LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS,
  EXPIRED_ONLY_TOUR_REFUND_ALLOWED_TYPES,
  EXPIRED_TOUR_REFUND_ALLOWED_TYPES,
  SERVICE_FEE_ALLOWED_TYPES,
  STEP_ADDITIONAL_INFO,
  SUBSCRIPTION_REASONS,
  TNFH_REASON_SOURCE,
} from '~/consts/refund';

import { RefundReasonCategorySectionTitle } from './RefundV2/Pages/components/RefundReasonCategorySectionTitle';

export default class SelectTypeOfRefund extends Component {
  componentDidMount() {
    this.props.setBackStep(null);
    this.props.setContinueAction(this.handler);
  }

  componentDidUpdate() {
    this.props.setContinueAction(this.handler);
  }

  componentWillUnmount() {
    this.props.setContinueAction(null);
  }

  handler = () => {
    let refund = this.props.refund;
    let setDefaultValuesForReason = this.props.setDefaultValuesForReason;
    this.props.stepHandler(function (error, next) {
      if (refund.reason === '') {
        error('A refund reason must be selected');
        return;
      }

      setDefaultValuesForReason();

      next(STEP_ADDITIONAL_INFO);
    });
  };

  render() {
    const itemType = this.props.itemType;
    const order = this.props.order;

    const isTour = itemType === ITEM_TYPE_TOUR;
    const vendorLabel = isTour ? 'tour provider' : 'vendor';

    let refundTypes;
    let reasons = COLLECTION_REASONS;

    if (this.props.refund.is_expired_tour_refund) {
      refundTypes = Object.keys(COLLECTION_REASONS).filter((key) => EXPIRED_TOUR_REFUND_ALLOWED_TYPES.includes(key));
    } else {
      switch (itemType) {
        case ITEM_TYPE_CAR_HIRE:
          refundTypes = Object.keys(CAR_HIRE_REASONS);
          reasons = CAR_HIRE_REASONS;
          break;
        case ITEM_TYPE_SERVICE_FEE:
          refundTypes = Object.keys(COLLECTION_REASONS).filter((key) => SERVICE_FEE_ALLOWED_TYPES.includes(key));
          break;
        case ITEM_TYPE_SUBSCRIPTION:
          refundTypes = Object.keys(SUBSCRIPTION_REASONS);
          reasons = SUBSCRIPTION_REASONS;
          break;
        default:
          refundTypes = Object.keys(COLLECTION_REASONS).filter(
            (key) => !EXPIRED_ONLY_TOUR_REFUND_ALLOWED_TYPES.includes(key),
          );
      }

      // Only show EXPIRED_BNBL_PACKAGE option for BNBL items
      if (!this.props.isBNBL) {
        refundTypes = refundTypes.filter((key) => key !== 'EXPIRED_BNBL_PACKAGE');
      }

      const hasAccommodation = !!order.accommodation_items.length || !!order.bedbank_items.length;
      const hasCruise = !!order.cruise_items.length;
      const hasTour = !!order.tour_items.length;

      if (hasAccommodation || hasCruise || hasTour) {
        if (!this.props.isRefundable) {
          // Change refund reason type when outside cancellation period
          if (reasons['INSURANCE_BREAKAGE']) {
            reasons['INSURANCE_BREAKAGE'] = {
              ...reasons['INSURANCE_BREAKAGE'],
              defaults: {
                ...reasons['INSURANCE_BREAKAGE'].defaults,
                source: TNFH_REASON_SOURCE,
              },
            };
          }
        }
      } else {
        refundTypes = refundTypes.filter((key) => key !== 'INSURANCE_BREAKAGE');
      }

      // Filter out "Outside Cancellation Policy" option if payment schedule exists
      if (this.props.paymentScheduleExists) {
        refundTypes = refundTypes.filter(
          (key) => key !== COLLECTION_REASON_KEY_LATE_CHANGE_OF_MIND_OR_BAD_EXPERIENCE_OR_UNFORTUNATE_EVENTS,
        );
      }
    }

    const refundReasonsCategories = refundTypes.reduce(
      (acc, key) => {
        const reason = reasons[key];
        if (!reason || reason.hidden) {
          return acc;
        }

        switch (reason.group) {
          case CollectionReasonGroup.STANDARD_REFUND:
            acc[CollectionReasonGroup.STANDARD_REFUND].push(key);
            break;
          case CollectionReasonGroup.COMPLEX_REFUND:
            acc[CollectionReasonGroup.COMPLEX_REFUND].push(key);
            break;
          default:
            acc[CollectionReasonGroup.UNCATEGORIZED].push(key);
            break;
        }

        return acc;
      },
      {
        [CollectionReasonGroup.STANDARD_REFUND]: [],
        [CollectionReasonGroup.COMPLEX_REFUND]: [],
        [CollectionReasonGroup.UNCATEGORIZED]: [],
      },
    );

    return (
      <Box>
        <FormControl id="reason">
          <FormLabel>Select Type of Refund</FormLabel>
          <RadioGroup name="reason" onChange={this.props.handleValueChange} value={this.props.refund.reason}>
            {refundReasonsCategories[CollectionReasonGroup.STANDARD_REFUND].length > 0 && (
              <Box mt={0}>
                <RefundReasonCategorySectionTitle
                  severity="success"
                  title="Standard Refund"
                  helperText={`(LE will not pay the ${vendorLabel})`}
                />
                <Box paddingLeft={2}>
                  {refundReasonsCategories[CollectionReasonGroup.STANDARD_REFUND].map((key) => (
                    <FormControlLabel
                      key={key}
                      value={reasons[key].key}
                      control={<Radio />}
                      label={
                        <span>
                          <Typography fontWeight="bold" component="span" display="block">
                            {reasons[key].label || reasons[key].key}
                          </Typography>
                          <Typography component="span" display="block">
                            {reasons[key].description}
                          </Typography>
                        </span>
                      }
                      disableTypography
                      sx={{ py: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {refundReasonsCategories[CollectionReasonGroup.COMPLEX_REFUND].length > 0 && (
              <Box mt={1}>
                <RefundReasonCategorySectionTitle
                  severity="error"
                  title="Complex Refund"
                  helperText={`(LE will pay the ${vendorLabel} a custom amount)`}
                />
                <Box paddingLeft={2}>
                  {refundReasonsCategories[CollectionReasonGroup.COMPLEX_REFUND].map((key) => (
                    <FormControlLabel
                      key={key}
                      value={reasons[key].key}
                      control={<Radio />}
                      label={
                        <span>
                          <Typography fontWeight="bold" component="span" display="block">
                            {reasons[key].label || reasons[key].key}
                          </Typography>
                          <Typography component="span" display="block">
                            {reasons[key].description}
                          </Typography>
                        </span>
                      }
                      disableTypography
                      sx={{ py: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {refundReasonsCategories[CollectionReasonGroup.UNCATEGORIZED].length > 0 && (
              <Box mt={1}>
                <RefundReasonCategorySectionTitle title="Other" severity="warning" />
                <Box paddingLeft={2}>
                  {refundReasonsCategories[CollectionReasonGroup.UNCATEGORIZED].map((key) => (
                    <FormControlLabel
                      key={key}
                      value={reasons[key].key}
                      control={<Radio />}
                      label={
                        <span>
                          <Typography fontWeight="bold" component="span" display="block">
                            {reasons[key].label || reasons[key].key}
                          </Typography>
                          <Typography component="span" display="block">
                            {reasons[key].description}
                          </Typography>
                        </span>
                      }
                      disableTypography
                      sx={{ py: 1 }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  }
}
